import React, { useState } from 'react';
import { generateCampaign } from '../api/generateCampaign';
import { parseCampaignOutput } from '../utils/parseCampaignOutput';
import ProspectingOutput from '../components/Prospecting/ProspectingOutput';
import { sharedStyles as styles } from '../styles/alfredStyles';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { getAuth } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import saveThreadToFirestore from '../utils/saveThreadToFirestore';

const frameworkSnippets = {
  phoneCall: {
    label: 'Phone Call Framework',
    description: 'Live call script with urgency, value, CTA'
  },
  voicemail: {
    label: 'Voicemail Framework',
    description: 'Quick, effective voicemail structure'
  },
  email: {
    label: 'Email Framework',
    description: 'Email format: hook, value, bridge, CTA'
  },
  video: {
    label: 'Video Message Framework',
    description: 'Entertain, educate, and CTA in 60s'
  },
  hookValueProofCTA: {
    label: 'Hook → Value → Proof → CTA',
    description: 'Relevant hook, value, proof, CTA'
  },
  softFollowUp: {
    label: 'Soft Touch Follow-Up',
    description: 'Warm follow-up referencing last message'
  },
  patternInterrupt: {
    label: 'Pattern Interrupt',
    description: 'Disrupt autopilot with a curveball'
  },
  urgencyReframe: {
    label: 'Urgency Reframe',
    description: 'Frame lost time or missed opportunity'
  }
};

const ProspectingForm = () => {
  const [inputs, setInputs] = useState({
    goal: '',
    industry: '',
    geography: '',
    persona: '',
    companySize: '',
    contactName: '',
    channels: [],
    tone: '',
    includePainPoint: false,
    painPoint: '',
    includeStats: false,
    mentionProductLine: false,
    productLine: '',
    selectedFrameworks: [],
    numTouches: 5 //
  });
  const [threadId, setThreadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dots, setDots] = useState('');
  const [campaignOutput, setCampaignOutput] = useState('');
  const [parsedOutput, setParsedOutput] = useState([]);
  const [initialAlfredMessage, setInitialAlfredMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      if (name === 'channels') {
        setInputs((prev) => ({
          ...prev,
          channels: checked
            ? [...prev.channels, value]
            : prev.channels.filter((ch) => ch !== value),
        }));
      } else {
        setInputs((prev) => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setInputs((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleToggle = (name) => {
    setInputs((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('🟡 Form submitted. Sending data to backend:', inputs);

    try {
      const result = await generateCampaign(inputs);
      console.log('🟢 Received result from backend:', result);

      if (result) {
        const parsed = parseCampaignOutput(result);
        setCampaignOutput(result);
        setParsedOutput(parsed);
        const message = {
          sender: 'alfred',
          text: `Hope the campaign targeting ${inputs.persona || 'unknown'}s in ${inputs.industry || 'unknown'} (${inputs.geography || 'unspecified'}) is a good starting point!`
        };
        setInitialAlfredMessage(message);

        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          const newThreadId = uuidv4();
          setThreadId(newThreadId);

          const metadata = {
            industry: inputs.industry,
            geography: inputs.geography,
            persona: inputs.persona,
            goal: inputs.goal,
            companySize: inputs.companySize
          };

          const nameParts = [];
          if (metadata.goal) nameParts.push(metadata.goal);
          if (metadata.industry) nameParts.push(metadata.industry);
          if (metadata.persona) nameParts.push(metadata.persona);
          if (metadata.geography) nameParts.push(metadata.geography);
          if (metadata.companySize) nameParts.push(`Size: ${metadata.companySize}`);
          const finalName = `Prospecting Campaign: ${nameParts.join(' – ') || 'Untitled'}`;

          await saveThreadToFirestore(user.uid, {
            id: newThreadId,
            name: finalName,
            pinned: false,
            folders: [],
            type: 'prospecting',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            data: {
              campaign: parsed,
              chat: [], // ✅ inject it HERE
              formInputs: inputs || {}
            },
            metadata
          });
        }
      } else {
        setCampaignOutput('');
        setParsedOutput([]);
      }
    } catch (err) {
      console.error('❌ Error during campaign generation:', err);
      setCampaignOutput('Error generating campaign.');
      setParsedOutput([]);
    }

    setLoading(false);
  };

  React.useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setDots(prev => (prev.length < 3 ? prev + '.' : ''));
      }, 500);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loading]);

  const toggleFramework = (key) => {
    setInputs((prev) => {
      const frameworks = prev.selectedFrameworks.includes(key)
        ? prev.selectedFrameworks.filter(f => f !== key)
        : [...prev.selectedFrameworks, key];
      return { ...prev, selectedFrameworks: frameworks };
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <h2 style={styles.heading}>Build a Prospecting Campaign</h2>
        <form onSubmit={handleSubmit}>
          {/* Campaign Goal */}
          <label style={styles.label}>Campaign Goal</label>
          <select name="goal" onChange={handleChange} style={styles.select} required>
            <option value="">Select a goal</option>
            <option value="Book a Meeting">Book a Meeting</option>
            <option value="Re-engage">Re-engage</option>
            <option value="Nurture">Nurture</option>
            <option value="Upsell">Upsell</option>
          </select>

          {/* Industry */}
          <div style={{ width: '100%' }}>
            <label style={styles.label}>Industry</label>
            <input type="text" name="industry" placeholder="e.g., Roofing, Law, Healthcare" onChange={handleChange} style={styles.input} required />
          </div>

          {/* Geography (Optional) */}
          <div style={{ width: '100%' }}>
            <label style={styles.label}>Geography (optional)</label>
            <input type="text" name="geography" placeholder="e.g., Florida, Midwest" onChange={handleChange} style={styles.input} />
          </div>

          {/* Persona */}
          <div style={{ width: '100%' }}>
            <label style={styles.label}>Target Role / Persona</label>
            <input type="text" name="persona" placeholder="e.g., Owner, CFO, Controller" onChange={handleChange} style={styles.input} required />
          </div>

          {/* Company Size */}
          <label style={styles.label}>Company Size (based on number of employees)</label>
          <select name="companySize" onChange={handleChange} style={styles.select}>
            <option value="">Select size</option>
            <option value="Micro">Micro (1–10 employees)</option>
            <option value="Small">Small (11–50 employees)</option>
            <option value="Midsize">Midsize (51–250 employees)</option>
            <option value="Large">Large (250+ employees)</option>
          </select>

          {/* Contact Name / LinkedIn */}
          <div style={{ width: '100%' }}>
            <label style={styles.label}>Contact Name or LinkedIn URL (optional)</label>
            <input type="text" name="contactName" placeholder="e.g., Alfred or www.linkedin.com/BigAl" onChange={handleChange} style={styles.input} />
          </div>

          {/* Number of Touches */}
          <label style={styles.label}>Number of Campaign Touches</label>
          <select
            name="numTouches"
            onChange={handleChange}
            value={inputs.numTouches}
            style={styles.select}
          >
            <option value="1">1 touches</option>
            <option value="2">2 touches</option>
            <option value="3">3 touches</option>
            <option value="4">4 touches</option>
            <option value="5">Default (5 touches)</option>
            <option value="6">6 touches</option>
            <option value="7">7 touches</option>
            <option value="8">8 touches</option>
          </select>

          {/* Channels */}
          <div style={styles.checkboxGroup}>
            <label style={styles.label}>Preferred Channels:</label>
            {[
              'Phone',
              'Voicemail',
              'Email',
              'SMS',
              'Video Script',
              'Face-to-Face Talking Points',
              'LinkedIn Message',
              'Handwritten Note'
            ].map((channel) => (
              <label key={channel} style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="channels"
                  value={channel}
                  onChange={handleChange}
                />{' '}
                {channel}
              </label>
            ))}
          </div>

          {/* Framework Selection */}
          <h4 style={{ ...styles.label, marginTop: '30px' }}>Best In Class Frameworks to Include:</h4>
          <div style={styles.checkboxGroup}>
            {Object.entries(frameworkSnippets).map(([key, snippet]) => (
              <label key={key} style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={inputs.selectedFrameworks.includes(key)}
                  onChange={() => toggleFramework(key)}
                />
                {snippet.label}
              </label>
            ))}
          </div>

          {/* Tone */}
          <label style={styles.label}>Tone</label>
          <select name="tone" onChange={handleChange} style={styles.select} required>
            <option value="">Select a tone</option>
            <option value="Professional">Professional</option>
            <option value="Friendly">Friendly</option>
            <option value="Bold">Bold</option>
            <option value="Consultative">Consultative</option>
          </select>

          {/* Optional Enhancements Section Title */}
          <h4 style={{ ...styles.label, marginTop: '30px' }}>Optional Enhancements:</h4>

          {/* Toggle: Include Pain Point or Offer */}
          <div style={styles.toggleRow}>
            <input
                type="checkbox"
                checked={inputs.includePainPoint}
                onChange={() => handleToggle('includePainPoint')}
            />
            <label style={{ fontWeight: '500' }}>Include Pain Point or Offer</label>
            <input
              type="text"
              name="painPoint"
              placeholder="e.g., Cash flow tied up in receivables, Waived fees"
              onChange={handleChange}
              value={inputs.painPoint}
              style={{ ...styles.input, flex: 2, marginBottom: 0 }}
            />
          </div>

          {/* Include Stats Toggle */}
          <div style={styles.toggleRow}>
            <input type="checkbox" checked={inputs.includeStats} onChange={() => handleToggle('includeStats')} />
            <label style={{ fontWeight: '500' }}>Include stats or benchmarks</label>
          </div>

          {/* Mention Product Line Toggle + Input */}
          <div style={styles.toggleRow}>
            <input
                type="checkbox"
                checked={inputs.mentionProductLine}
                onChange={() => handleToggle('mentionProductLine')}
            />
            <label style={{ fontWeight: '500' }}>Mention product line XYZ</label>
            <input
              type="text"
              name="productLine"
              placeholder="e.g., Fast Funding Program, SBA Loan, Payee Positive Pay"
              onChange={handleChange}
              value={inputs.productLine}
              style={{ ...styles.input, flex: 2, marginBottom: 0 }}
            />
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? `Alfred is Generating Your Campaign${dots}✏️` : 'Generate Campaign'}
          </button>
        </form>

        {parsedOutput.length > 0 && (
          <ProspectingOutput
            campaignData={parsedOutput}
            existingThreadId={threadId}
            existingFormData={inputs}
            existingChatHistory={[]}
          />
        )}
      </div>
    </div>
  );



};

export default ProspectingForm;
